import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtDate, apiErr } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { toast } from "sonner";
import { PlusIcon, BuildingsIcon, MapPinIcon, PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const emptyCo = { codigo: "", nombre: "", razon_social: "", rtn: "", direccion: "", telefono: "", email: "", moneda: "HNL", color: "#02BF44" };
const emptyBr = { codigo: "", nombre: "", direccion: "", telefono: "", manager: "" };

export default function Companies() {
  const { user } = useAuth();
  const { refresh } = useCompany();
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState({});
  const [openCo, setOpenCo] = useState(false);
  const [editCo, setEditCo] = useState(null);
  const [formCo, setFormCo] = useState(emptyCo);
  const [openBr, setOpenBr] = useState(null); // {company_id}
  const [editBr, setEditBr] = useState(null);
  const [formBr, setFormBr] = useState(emptyBr);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    const { data: cos } = await api.get("/companies");
    setCompanies(cos);
    const map = {};
    for (const c of cos) {
      try { const { data } = await api.get("/branches", { params: { company_id: c.id } }); map[c.id] = data; }
      catch { map[c.id] = []; }
    }
    setBranches(map);
  };
  useEffect(() => { load(); }, []);

  const saveCo = async (e) => {
    e.preventDefault();
    try {
      if (editCo) await api.put(`/companies/${editCo.id}`, formCo);
      else await api.post("/companies", formCo);
      toast.success("Empresa guardada");
      setOpenCo(false); setEditCo(null); setFormCo(emptyCo);
      load(); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const cm = useModalForm({ value: formCo, abierto: openCo, onClose: () => setOpenCo(false), onSave: (e) => saveCo(e) });
  const bm = useModalForm({ value: formBr, abierto: Boolean(openBr), onClose: () => setOpenBr(null), onSave: (e) => saveBr(e) });
  const saveBr = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formBr, company_id: openBr };
      if (editBr) await api.put(`/branches/${editBr.id}`, payload);
      else await api.post("/branches", payload);
      toast.success("Sucursal guardada");
      setOpenBr(null); setEditBr(null); setFormBr(emptyBr);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(`¿Eliminar empresa "${c.nombre}" (${c.codigo})?\n\nSi tiene datos vinculados (facturas, clientes, productos…) se desactivará en lugar de borrarse para preservar la integridad.`)) return;
    try {
      const { data } = await api.delete(`/companies/${c.id}`);
      if (data.disabled) {
        const used = Object.entries(data.counts || {}).filter(([_, v]) => v > 0).map(([k, v]) => `${k}: ${v}`).join(", ");
        toast.success(`Empresa desactivada (datos vinculados: ${used})`);
      } else {
        toast.success(`Empresa eliminada${data.branches_deleted ? ` (+ ${data.branches_deleted} sucursales)` : ""}`);
      }
      load(); refresh?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteBranch = async (b) => {
    if (!window.confirm(`¿Eliminar sucursal "${b.nombre}" (${b.codigo})?`)) return;
    try {
      const { data } = await api.delete(`/branches/${b.id}`);
      toast.success(data.disabled ? "Sucursal desactivada (tiene datos)" : "Sucursal eliminada");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Layout
      title="Empresas y Sucursales"
      subtitle={`${companies.length} empresas · ${Object.values(branches).reduce((s, a) => s + a.length, 0)} sucursales`}
      actions={
        isAdmin && (
          <button onClick={() => { setEditCo(null); setFormCo(emptyCo); setOpenCo(true); }} data-testid="new-company-btn"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
            <PlusIcon size={16} weight="bold" /> Nueva empresa
          </button>
        )
      }
    >
      <div className="space-y-5">
        {companies.map((c) => (
          <div key={c.id} className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid={`company-card-${c.codigo}`}>
            <div className="p-5 border-b border-border/60 flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-lg flex items-center justify-center font-heading font-bold text-black text-xl flex-shrink-0" style={{ backgroundColor: c.color || "#02BF44" }}>
                  {c.codigo.slice(0, 2)}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <BuildingsIcon size={14} className="text-agro-green" weight="duotone" />
                    <span className="text-[10px] uppercase tracking-wider text-agro-green font-medium">{c.codigo}</span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-text-primary">{c.nombre}</h3>
                  <div className="text-xs text-text-secondary mt-1">{c.razon_social}</div>
                  <div className="text-xs text-text-muted mt-2 flex gap-3 flex-wrap">
                    <span>RTN: <span className="font-mono">{c.rtn || "—"}</span></span>
                    <span>{c.telefono || "—"}</span>
                    <span>{c.email || "—"}</span>
                    <span>{c.moneda}</span>
                  </div>
                  <div className="text-xs text-text-muted mt-1">{c.direccion}</div>
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <button onClick={() => { setEditCo(c); setFormCo({ ...emptyCo, ...c }); setOpenCo(true); }} data-testid={`edit-company-${c.codigo}`}
                    title="Editar empresa"
                    className="p-2 text-text-muted hover:text-agro-green border border-border rounded-md"><PencilSimpleIcon size={14} /></button>
                  <button onClick={() => deleteCompany(c)} data-testid={`delete-company-${c.codigo}`}
                    title="Eliminar empresa"
                    className="p-2 text-text-muted hover:text-red-400 border border-border rounded-md hover:border-red-500/40"><TrashIcon size={14} /></button>
                  <button onClick={() => { setOpenBr(c.id); setEditBr(null); setFormBr(emptyBr); }} data-testid={`add-branch-${c.codigo}`}
                    className="px-3 py-2 text-xs border border-border rounded-md text-text-secondary hover:text-agro-yellow hover:border-agro-yellow/40 flex items-center gap-1.5">
                    <PlusIcon size={12} weight="bold" /> Sucursal
                  </button>
                </div>
              )}
            </div>

            <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(branches[c.id] || []).length === 0 ? (
                <div className="col-span-full text-center text-text-muted text-sm py-6">Sin sucursales</div>
              ) : (branches[c.id] || []).map((b) => (
                <div key={b.id} className="bg-app border border-border/60 rounded-md p-3 flex items-start gap-3" data-testid={`branch-${b.codigo}`}>
                  <div className="w-9 h-9 rounded-md flex items-center justify-center bg-agro-yellow/10 text-agro-yellow text-[10px] font-mono font-bold flex-shrink-0">{b.codigo}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary truncate">{b.nombre}</div>
                    <div className="text-[11px] text-text-muted truncate">{b.manager || "—"}</div>
                    <div className="text-[10px] text-text-muted truncate flex items-center gap-1 mt-0.5"><MapPinIcon size={10} /> {b.direccion || "—"}</div>
                  </div>
                  {isAdmin && (
                    <div className="flex flex-col gap-1">
                      <button onClick={() => { setOpenBr(c.id); setEditBr(b); setFormBr({ ...emptyBr, ...b }); }} className="text-text-muted hover:text-agro-green" title="Editar sucursal">
                        <PencilSimpleIcon size={12} />
                      </button>
                      <button onClick={() => deleteBranch(b)} className="text-text-muted hover:text-red-400" title="Eliminar sucursal" data-testid={`delete-branch-${b.codigo}`}>
                        <TrashIcon size={12} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {openCo && (
        <Modal title={editCo ? "Editar empresa" : "Nueva empresa"} m={cm}>
          <form onSubmit={cm.guardar} className="grid grid-cols-2 gap-4">
            <F label="Código *" v={formCo.codigo} on={(v) => setFormCo({...formCo, codigo:v})} required testid="company-codigo" />
            <F label="Nombre *" v={formCo.nombre} on={(v) => setFormCo({...formCo, nombre:v})} required testid="company-nombre" />
            <div className="col-span-2"><F label="Razón social" v={formCo.razon_social} on={(v) => setFormCo({...formCo, razon_social:v})} /></div>
            <F label="RTN" v={formCo.rtn} on={(v) => setFormCo({...formCo, rtn:v})} />
            <F label="Moneda" v={formCo.moneda} on={(v) => setFormCo({...formCo, moneda:v})} />
            <F label="Teléfono" v={formCo.telefono} on={(v) => setFormCo({...formCo, telefono:v})} />
            <F label="Email" v={formCo.email} on={(v) => setFormCo({...formCo, email:v})} />
            <div className="col-span-2"><F label="Dirección" v={formCo.direccion} on={(v) => setFormCo({...formCo, direccion:v})} /></div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Color</label>
              <input type="color" value={formCo.color} onChange={(e) => setFormCo({...formCo, color: e.target.value})} className="w-full h-10 bg-app border border-border rounded-md cursor-pointer" />
            </div>
            <div className="col-span-2 flex justify-end gap-3 pt-3 border-t border-border/60">
              <button type="button" onClick={cm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
              <button type="submit" data-testid="company-save" disabled={cm.saving} className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Guardar</button>
            </div>
          </form>
        </Modal>
      )}

      {openBr && (
        <Modal title={editBr ? "Editar sucursal" : "Nueva sucursal"} m={bm}>
          <form onSubmit={bm.guardar} className="grid grid-cols-2 gap-4">
            <F label="Código *" v={formBr.codigo} on={(v) => setFormBr({...formBr, codigo:v})} required testid="branch-codigo" />
            <F label="Nombre *" v={formBr.nombre} on={(v) => setFormBr({...formBr, nombre:v})} required />
            <F label="Manager" v={formBr.manager} on={(v) => setFormBr({...formBr, manager:v})} />
            <F label="Teléfono" v={formBr.telefono} on={(v) => setFormBr({...formBr, telefono:v})} />
            <div className="col-span-2"><F label="Dirección" v={formBr.direccion} on={(v) => setFormBr({...formBr, direccion:v})} /></div>
            <div className="col-span-2 flex justify-end gap-3 pt-3 border-t border-border/60">
              <button type="button" onClick={bm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
              <button type="submit" data-testid="branch-save" disabled={bm.saving} className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
}

function Modal({ title, m, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-border flex justify-between sticky top-0 bg-card">
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
function F({ label, v, on, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">{label}</label>
      <input data-testid={testid} type={type} value={v ?? ""} required={required} onChange={(e) => on(e.target.value)}
        className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
    </div>
  );
}
